import React, { createContext, useContext, useEffect, useMemo, useState, useCallback } from 'react';
import { DEFAULT_LANGUAGE, detectBrowserLanguage, isSupportedLanguage, isRtlLanguage } from './languages';

const STORAGE_KEY = 'ewu-language';

type TranslationDict = Record<string, string>;

interface LanguageContextValue {
  language: string;
  setLanguage: (code: string) => void;
  /** Translate a string of English source text. Falls back to the original text if no translation is found. */
  t: (source: string) => string;
  loading: boolean;
}

const LanguageContext = createContext<LanguageContextValue | undefined>(undefined);

function toPigLatin(source: string): string {
  return source.replace(/[A-Za-z]+/g, word => {
    const leadingConsonants = word.match(/^[^aeiouAEIOU]+/);
    const translated = leadingConsonants
      ? `${word.slice(leadingConsonants[0].length)}${leadingConsonants[0]}ay`
      : `${word}way`;
    return /^[A-Z]/.test(word)
      ? translated.charAt(0).toUpperCase() + translated.slice(1)
      : translated;
  });
}

function getInitialLanguage(): string {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored && isSupportedLanguage(stored)) return stored;
  } catch {
    // localStorage unavailable — ignore and fall through to detection
  }
  return detectBrowserLanguage();
}

export const LanguageProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [language, setLanguageState] = useState<string>(getInitialLanguage);
  const [dict, setDict] = useState<TranslationDict>({});
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // English is the source language — no lookup file needed, dict stays empty
    // and t() falls back to the original source text for every string.
    if (language === DEFAULT_LANGUAGE) {
      setDict({});
      setLoading(false);
      return;
    }

    let cancelled = false;
    setLoading(true);

    fetch(`/locales/${language}.json?t=${Date.now()}`)
      .then(r => {
        if (!r.ok) throw new Error(`HTTP ${r.status}`);
        return r.json();
      })
      .then((data: TranslationDict) => {
        if (!cancelled) {
          setDict(data);
          setLoading(false);
        }
      })
      .catch(() => {
        // Missing/broken translation file — fall back to English source text
        if (!cancelled) {
          setDict({});
          setLoading(false);
        }
      });

    return () => { cancelled = true; };
  }, [language]);

  useEffect(() => {
    document.documentElement.lang = language;
    document.documentElement.dir = isRtlLanguage(language) ? 'rtl' : 'ltr';
  }, [language]);

  const setLanguage = useCallback((code: string) => {
    if (!isSupportedLanguage(code)) return;
    setLanguageState(code);
    try {
      localStorage.setItem(STORAGE_KEY, code);
    } catch {
      // ignore write failures (private browsing, etc.)
    }
  }, []);

  const t = useCallback((source: string): string => {
    if (dict[source]) return dict[source];
    return language === 'pig' ? toPigLatin(source) : source;
  }, [dict, language]);

  const value = useMemo(
    () => ({ language, setLanguage, t, loading }),
    [language, setLanguage, t, loading]
  );

  return <LanguageContext.Provider value={value}>{children}</LanguageContext.Provider>;
};

export function useTranslation(): LanguageContextValue {
  const ctx = useContext(LanguageContext);
  if (!ctx) {
    throw new Error('useTranslation must be used within a LanguageProvider');
  }
  return ctx;
}
